import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import ImageUpload from './ImageUpload';
import ImagePositionEditor from './ImagePositionEditor';
import './FeaturedProgramsAdmin.css';

const PageHeaderAdmin = ({ contentPath }) => {
  const { getContent, updateContent } = useContent();
  const [sectionData, setSectionData] = useState(null);
  const [editingImage, setEditingImage] = useState(false);

  useEffect(() => {
    const data = getContent(contentPath);
    setSectionData(data);
  }, [contentPath, getContent]);

  const handleUpdate = (field, value) => {
    updateContent(`${contentPath}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleImagePositionSave = (settings) => {
    updateContent(`${contentPath}.heroImageSettings`, settings);
    setSectionData(getContent(contentPath));
    setEditingImage(false);
  };

  if (!sectionData) {
    return (
      <div className="programs-admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="page-header-admin">
      <div className="admin-section-header">
        <div className="section-info">
          <h2>
            <i className="fas fa-image"></i>
            En-tête de page
          </h2>
          <p>Gérez le titre, sous-titre et l'image hero de cette page</p>
        </div>
      </div>

      {/* Title & Subtitle */}
      <div className="section-settings">
        <div className="setting-group">
          <label>Titre</label>
          <input
            type="text"
            value={sectionData.title || ''}
            onChange={(e) => handleUpdate('title', e.target.value)}
            placeholder="Titre de la page..."
          />
        </div>
        
        <div className="setting-group">
          <label>Sous-titre</label>
          <textarea
            value={sectionData.subtitle || ''}
            onChange={(e) => handleUpdate('subtitle', e.target.value)}
            placeholder="Description de la page..."
            rows={3}
          />
        </div>
      </div>

      {/* Hero Image Section */}
      <div className="section-settings" style={{ marginTop: '30px' }}>
        <div className="setting-group full-width">
          <label style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>
            <i className="fas fa-image" style={{ marginRight: '8px' }}></i>
            Image Hero (Bannière de fond)
          </label>
          
          <ImageUpload
            value={sectionData.heroImage}
            onChange={(imageData) => handleUpdate('heroImage', imageData)}
            label="Image hero de la page"
          />
          
          {/* Image Position Editor Button */}
          {sectionData.heroImage && !editingImage && (
            <button
              type="button"
              onClick={() => setEditingImage(true)}
              style={{
                marginTop: '10px',
                padding: '10px 15px',
                backgroundColor: '#00bcd4',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <i className="fas fa-crop-alt" style={{ marginRight: '8px' }}></i>
              Ajuster position et zoom de l'image hero
            </button>
          )}

          {/* Image Position Editor */}
          {sectionData.heroImage && editingImage && (
            <div style={{
              marginTop: '15px',
              padding: '20px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '2px solid #00bcd4'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px'
              }}>
                <h4 style={{ margin: 0, color: '#00bcd4' }}>
                  <i className="fas fa-crop-alt" style={{ marginRight: '8px' }}></i>
                  Ajuster l'image hero
                </h4>
                <button
                  type="button"
                  onClick={() => setEditingImage(false)}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  <i className="fas fa-times" style={{ marginRight: '5px' }}></i>
                  Fermer
                </button>
              </div>
              <ImagePositionEditor
                imageUrl={sectionData.heroImage}
                currentSettings={{
                  ...sectionData.heroImageSettings,
                  aspectRatio: sectionData.heroImageSettings?.aspectRatio || '21:9'
                }}
                onSave={handleImagePositionSave}
                availableAspectRatios={['21:9']}
                previewDimensions={{ width: '100%', height: '300px' }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Overlay Settings Section */}
      <div className="section-settings" style={{ marginTop: '30px', borderTop: '2px solid #e0e0e0', paddingTop: '30px' }}>
        <div className="setting-group full-width">
          <label style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px', display: 'block' }}>
            <i className="fas fa-layer-group" style={{ marginRight: '8px' }}></i>
            Overlay coloré (couche au-dessus de l'image)
          </label>
          
          {/* Enable/Disable Overlay */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={sectionData.overlayEnabled !== false}
                onChange={(e) => handleUpdate('overlayEnabled', e.target.checked)}
                style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: '500' }}>Activer l'overlay coloré</span>
            </label>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginLeft: '26px' }}>
              Désactivez pour afficher l'image sans filtre coloré
            </p>
          </div>

          {/* Overlay Color */}
          {sectionData.overlayEnabled !== false && (
            <>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Couleur de l'overlay
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="color"
                    value={sectionData.overlayColor || '#58c4dc'}
                    onChange={(e) => handleUpdate('overlayColor', e.target.value)}
                    style={{ 
                      width: '60px', 
                      height: '40px', 
                      border: '2px solid #ddd', 
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  />
                  <input
                    type="text"
                    value={sectionData.overlayColor || '#58c4dc'}
                    onChange={(e) => handleUpdate('overlayColor', e.target.value)}
                    placeholder="#58c4dc"
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  Couleur par défaut: #58c4dc (bleu turquoise)
                </p>
              </div>

              {/* Overlay Opacity */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Opacité de l'overlay: {Math.round((sectionData.overlayOpacity ?? 0.85) * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={sectionData.overlayOpacity ?? 0.85}
                  onChange={(e) => handleUpdate('overlayOpacity', parseFloat(e.target.value))}
                  style={{ width: '100%', cursor: 'pointer' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  <span>Transparent (0%)</span>
                  <span>Opaque (100%)</span>
                </div>
              </div>

              {/* Preview */}
              {sectionData.heroImage && (
                <div style={{ marginTop: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                    Aperçu de l'overlay
                  </label>
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '150px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '2px solid #ddd'
                  }}>
                    <img
                      src={sectionData.heroImage}
                      alt="Preview"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: sectionData.overlayColor || '#58c4dc',
                      opacity: sectionData.overlayOpacity ?? 0.85
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '18px',
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      zIndex: 2
                    }}>
                      Texte de la bannière
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Hero action buttons (shared with /formations pageHeader) */}
      <div className="section-settings" style={{ marginTop: '30px', borderTop: '2px solid #e0e0e0', paddingTop: '30px' }}>
        <div className="setting-group full-width">
          <label style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px', display: 'block' }}>
            <i className="fas fa-hand-pointer" style={{ marginRight: '8px' }} />
            Boutons d&apos;action (sous le titre)
          </label>
          <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
            Affichés sur la page Formations et les pages spécialité. Liens internes (/formations), ancres (#registration-form) ou URLs externes.
          </p>

          {(sectionData.buttons || []).map((button, index) => (
            <div
              key={index}
              style={{
                marginBottom: '1rem',
                padding: '1rem',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                background: '#fafafa',
              }}
            >
              <div className="setting-group">
                <label>Texte</label>
                <input
                  type="text"
                  value={button.text || ''}
                  onChange={(e) => {
                    const next = [...(sectionData.buttons || [])];
                    next[index] = { ...next[index], text: e.target.value };
                    handleUpdate('buttons', next);
                  }}
                  placeholder="Voir le calendrier"
                />
              </div>
              <div className="setting-group">
                <label>Lien</label>
                <input
                  type="text"
                  value={button.link || ''}
                  onChange={(e) => {
                    const next = [...(sectionData.buttons || [])];
                    next[index] = { ...next[index], link: e.target.value };
                    handleUpdate('buttons', next);
                  }}
                  placeholder="/formations#registration-form"
                />
              </div>
              <div className="setting-group">
                <label>Icône FontAwesome</label>
                <input
                  type="text"
                  value={button.icon || ''}
                  onChange={(e) => {
                    const next = [...(sectionData.buttons || [])];
                    next[index] = { ...next[index], icon: e.target.value };
                    handleUpdate('buttons', next);
                  }}
                  placeholder="fas fa-calendar"
                />
              </div>
              <div className="setting-group">
                <label>Type</label>
                <select
                  value={button.type || 'primary'}
                  onChange={(e) => {
                    const next = [...(sectionData.buttons || [])];
                    next[index] = { ...next[index], type: e.target.value };
                    handleUpdate('buttons', next);
                  }}
                >
                  <option value="primary">Primary</option>
                  <option value="outline">Outline</option>
                </select>
              </div>
              <button
                type="button"
                onClick={() => {
                  const next = (sectionData.buttons || []).filter((_, i) => i !== index);
                  handleUpdate('buttons', next);
                }}
                style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
              >
                <i className="fas fa-trash" style={{ marginRight: '6px' }} />
                Supprimer
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={() =>
              handleUpdate('buttons', [
                ...(sectionData.buttons || []),
                { text: '', link: '', icon: 'fas fa-arrow-right', type: 'primary' },
              ])
            }
            style={{
              padding: '10px 16px',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: '500',
            }}
          >
            <i className="fas fa-plus" style={{ marginRight: '8px' }} />
            Ajouter un bouton
          </button>
        </div>
      </div>
    </div>
  );
};

export default PageHeaderAdmin;
